// src/components/my-journey/SeasonStoryDeck.jsx
// Season Story Mode — 12 slides across 6 sections.

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLeagueData, useManagerContext } from '../../context/DataContext';
import ShareableSeasonCard from './ShareableSeasonCard';

// ── Helpers ────────────────────────────────────────────────────────────────────
const getOrdinalSuffix = (n) => {
  if (!n || isNaN(n)) return '';
  const j = n % 10, k = n % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

// monthlyPoints contains every manager's total per month (Manager, Month, Points).
// monthlyTopPerformers contains only the monthly winner per month.
// We use monthlyPoints when available so every manager gets their personal best month,
// falling back to monthlyTopPerformers for backwards-compatibility with cached/Excel data.
const deriveBestMonth = (monthlyPoints, managerName, monthlyTopPerformers) => {
  const source = monthlyPoints?.length ? monthlyPoints : (monthlyTopPerformers || []);
  if (!source.length) return 'Unknown';
  const entries = source.filter((r) => r.Manager === managerName);
  if (!entries.length) return 'Unknown';
  const bestEntry = entries.reduce(
    (acc, cur) => (Number(cur.Points || 0) > Number(acc.Points || 0) ? cur : acc),
    entries[0]
  );
  // Month value from the scraper is formatted as "August 2025" — strip the year
  // so the slide shows only the month name (e.g. "August").
  const rawMonth = bestEntry.Month || 'Unknown';
  return rawMonth !== 'Unknown' ? rawMonth.replace(/\s+\d{4}$/, '') : 'Unknown';
};

// ── Motion ─────────────────────────────────────────────────────────────────────
const slideVariants = {
  enter: { opacity: 0, y: 40, scale: 0.97 },
  centre: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -40, scale: 1.02 },
};
const slideTransition = { duration: 0.45, ease: [0.22, 1, 0.36, 1] };

// ── Section map ────────────────────────────────────────────────────────────────
const SECTION_MAP = [
  'Your Season',    // 0
  'Your Season',    // 1
  'Your Season',    // 2
  'The Journey',    // 3
  'The Journey',    // 4
  'Your Decisions', // 5
  'Your Decisions', // 6
  'Your Decisions', // 7
  'Your Edge',      // 8
  'Your Edge',      // 9
  'Who You Are',    // 10
  'The Verdict',    // 11
];

// ── Archetype info map ─────────────────────────────────────────────────────────
const ARCHETYPE_INFO = {
  'Captain Fantastic':     { description: 'You have a knack for choosing the right captain at the right time. Your captain selections consistently deliver big hauls, showcasing your excellent player judgment.', image: '/images/character-classes/captain-fantastic.jpg' },
  'Warren Buffett':        { description: "You're the financial wizard of FPL! Your team value consistently outperforms others, showing your market savvy and investment acumen.", image: '/images/character-classes/warren-buffett.jpg' },
  'King of the Hill':      { description: "The undisputed ruler of your FPL league. You've dominated from the top position, showing mastery of all FPL elements.", image: '/images/character-classes/king-of-the-hill.jpg' },
  'The Bridesmaid':        { description: "So close, yet so far! You've been one of the top contenders all season, always in sight of the crown but not quite wearing it.", image: '/images/character-classes/the-bridesmaid.jpg' },
  'The Slump King':        { description: "Nobody endures a downturn quite like you. You've weathered significant slumps during the season, showing remarkable resilience.", image: '/images/character-classes/the-slump-king.jpg' },
  'The Glory Hunter':      { description: "When you're hot, you're scorching! You've enjoyed exceptional stretches of above-average performance in the league.", image: '/images/character-classes/the-glory-hunter.jpg' },
  'The Grinder':           { description: "When life knocks you down, you get back up stronger. You've shown remarkable recovery after difficult periods, displaying true grit.", image: '/images/character-classes/the-grinder.jpg' },
  'The Closer':            { description: 'When it matters most, you deliver. Your end-of-season performance stands out, showing your clutch decision-making when the pressure is on.', image: '/images/character-classes/the-closer.jpg' },
  'The Fortune Teller':    { description: "Luck? What luck? Your uncanny ability to make the right moves at the right time suggests you can see the future.", image: '/images/character-classes/the-fortune-teller.jpg' },
  'The Unlucky General':   { description: "The fantasy gods have not smiled upon you. Despite your best plans, fate has conspired against you with unfortunate timing and bad luck.", image: '/images/character-classes/the-unlucky-general.jpg' },
  'The Mastermind':        { description: "Your strategic brilliance has been on full display. You consistently make moves that are one step ahead of the competition.", image: '/images/character-classes/the-mastermind.jpg' },
  'The Risk Taker':        { description: "Fortune favors the bold! Your willingness to make differential picks and unconventional choices has set you apart from the crowd.", image: '/images/character-classes/the-risk-taker.jpg' },
  'The Steadfast':         { description: 'Consistency is your hallmark. While others ride the rollercoaster of FPL emotions, you maintain a steady course.', image: '/images/character-classes/the-steadfast.jpg' },
  'The Armband Whisperer': { description: 'You have an innate sense for captain selection, consistently making good choices that minimise missed opportunities.', image: '/images/character-classes/the-armband-whisperer.jpg' },
  'The Treasury Keeper':   { description: 'A master of the FPL market, you build and maintain team value with skill, creating a strong financial foundation for your squad.', image: '/images/character-classes/the-treasury-keeper.jpg' },
  'The Silver Knight':     { description: 'A noble competitor who consistently finishes in the upper ranks of the league, respected for your reliable performance.', image: '/images/character-classes/the-silver-knight.jpg' },
  'The Dark Horse':        { description: "Unpredictable and capable of brilliance when least expected. While you may not rank at the very top overall, on your day, you're unstoppable.", image: '/images/character-classes/the-dark-horse.jpg' },
  'The Battle-Scarred':    { description: "You've endured significant trials during the FPL season, weathering difficult periods below average but emerging stronger for the experience.", image: '/images/character-classes/the-battle-scarred.jpg' },
  'The Phoenix':           { description: "Rising from the ashes of defeat, you've transformed your season after a slump, demonstrating remarkable recovery powers.", image: '/images/character-classes/the-phoenix.jpg' },
  'The Endgame Tactician': { description: 'When the season enters its final phase, you find another gear. Your strategic prowess in the endgame is formidable.', image: '/images/character-classes/the-endgame-tactician.jpg' },
  'The Star-Crossed':      { description: "Fate seems to conspire against your best-laid plans, with luck rarely falling in your favour despite sound decision-making.", image: '/images/character-classes/the-star-crossed.jpg' },
  'The Favored One':       { description: 'Fortune smiles upon your FPL journey, with the fantasy gods regularly blessing your decisions with favourable outcomes.', image: '/images/character-classes/the-favored-one.jpg' },
  'The Maverick':          { description: 'A bold risk-taker who forges their own path, ignoring template teams in favour of differential selections that others overlook.', image: '/images/character-classes/the-maverick.jpg' },
  'The Tactician':         { description: 'Balancing risk and reward with precision, you make calculated differential selections that give you an edge without excessive gambles.', image: '/images/character-classes/the-tactician.jpg' },
  'The Oracle':            { description: 'Your conservative approach to player selection reveals a deeper wisdom about the game, focusing on reliable performers.', image: '/images/character-classes/the-oracle.jpg' },
  'The Guardian':          { description: 'Steady and reliable, you protect your FPL rank through consistent decision-making without wild swings in performance.', image: '/images/character-classes/the-guardian.jpg' },
  'The Wildborn':          { description: 'Untamed and unpredictable, your season is marked by dramatic swings in fortune, creating an exciting yet chaotic journey.', image: '/images/character-classes/the-wildborn.jpg' },
};

// ── Shared UI ──────────────────────────────────────────────────────────────────
const DotBackground = () => (
  <div style={{
    position: 'absolute', inset: 0,
    backgroundImage: 'radial-gradient(circle, rgba(63,105,174,0.18) 1.5px, transparent 1.5px)',
    backgroundSize: '28px 28px', pointerEvents: 'none',
  }} />
);

const ProgressDots = ({ total, current }) => (
  <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap', maxWidth: '200px' }}>
    {Array.from({ length: total }, (_, i) => (
      <div key={i} style={{
        width: i === current ? '16px' : '5px', height: '5px', borderRadius: '3px',
        backgroundColor: i === current ? '#44d5a3' : 'rgba(255,255,255,0.2)',
        transition: 'all 0.3s ease',
      }} />
    ))}
  </div>
);

const SlideLabel = ({ children, color = '#8a8a8a' }) => (
  <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
    style={{ fontSize: '14px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color }}>
    {children}
  </motion.p>
);

// ═══════════════════════════════════════════════════════════
// SECTION 1 — YOUR SEASON
// ═══════════════════════════════════════════════════════════

const IntroSlide = ({ managerName }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 gap-6">
    <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#44d5a3' }}>
      FPL Wrapped
    </motion.p>
    <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
      style={{ fontSize: 'clamp(28px,6vw,48px)', fontWeight: 900, color: '#ffffff', lineHeight: 1.15 }}>
      Your 2025/26<br />FPL Season
    </motion.h1>
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
      style={{ fontSize: 'clamp(18px,4vw,28px)', fontWeight: 700, color: '#3f69ae' }}>
      {managerName}
    </motion.p>
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
      style={{ fontSize: '13px', color: '#8a8a8a' }}>
      Tap to continue →
    </motion.p>
  </div>
);

const RankSlide = ({ leaguePosition, totalPoints }) => {
  const positionNum = parseInt(leaguePosition, 10) || null;
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8 gap-4">
      <SlideLabel>You finished</SlideLabel>
      <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.25, type: 'spring', stiffness: 180, damping: 20 }} style={{ lineHeight: 1 }}>
        <span style={{ fontSize: 'clamp(72px,18vw,120px)', fontWeight: 900, color: '#3f69ae' }}>
          {positionNum || leaguePosition}
        </span>
        {positionNum && (
          <span style={{ fontSize: 'clamp(28px,7vw,48px)', fontWeight: 900, color: '#3f69ae', verticalAlign: 'super' }}>
            {getOrdinalSuffix(positionNum)}
          </span>
        )}
      </motion.div>
      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
        style={{ fontSize: 'clamp(16px,4vw,22px)', fontWeight: 700, color: '#44d5a3' }}>
        {totalPoints.toLocaleString()} points
      </motion.p>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.75 }}
        style={{ fontSize: '12px', color: '#8a8a8a' }}>
        Overall league standing
      </motion.p>
    </div>
  );
};

const BestMonthSlide = ({ bestMonth }) => (
  <div className="flex flex-col items-center justify-center h-full text-center px-8 gap-5">
    <SlideLabel>Your best month was</SlideLabel>
    <motion.h2 initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.3, type: 'spring', stiffness: 160, damping: 18 }}
      style={{ fontSize: 'clamp(44px,11vw,80px)', fontWeight: 900, color: '#44d5a3', lineHeight: 1 }}>
      {bestMonth}
    </motion.h2>
    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}
      style={{ fontSize: '14px', color: '#8a8a8a' }}>
      When you hit your peak form
    </motion.p>
  </div>
);

// ═══════════════════════════════════════════════════════════
// SECTION 2 — THE JOURNEY
// ═══════════════════════════════════════════════════════════

// ── League position journey chart ─────────────────────────────────────────────
// Computes cumulative league position for each GW incrementally (O(38×n)).
// Draws a smooth bezier path that animates via SVG pathLength, then fades in
// the manager's immediate neighbours at the final standings.
const SeasonMomentumSlide = ({ weeklyPoints, managerName, transferCostsGW }) => {
  const allManagers = weeklyPoints || [];
  const numManagers = allManagers.length;

  // Build transfer cost lookup: manager name → row object keyed by GW number
  const transferLookup = {};
  (transferCostsGW || []).forEach(row => { transferLookup[row.Manager] = row; });

  // Find last GW with data for this manager
  const managerRow = allManagers.find((r) => r.Manager === managerName);
  let lastGW = 0;
  if (managerRow) {
    for (let gw = 38; gw >= 1; gw--) {
      if (managerRow[gw] !== undefined && !isNaN(Number(managerRow[gw]))) { lastGW = gw; break; }
    }
  }

  // Incrementally build cumulative scores → rank per GW
  // Points are net of transfer penalties so rankings match the actual standings
  const cumScores = allManagers.map(() => 0);
  const gwPositions = []; // [{gw, position, sorted:[{name,cum}]}]
  for (let gw = 1; gw <= lastGW; gw++) {
    allManagers.forEach((row, i) => {
      const val = Number(row[gw]);
      if (!isNaN(val)) cumScores[i] += val;
      const penalty = Number((transferLookup[row.Manager] || {})[gw] || 0);
      if (!isNaN(penalty)) cumScores[i] -= penalty;
    });
    const snapshot = allManagers.map((row, i) => ({ name: row.Manager, cum: cumScores[i] }));
    const sorted = [...snapshot].sort((a, b) => b.cum - a.cum);
    const myIdx = sorted.findIndex((s) => s.name === managerName);
    gwPositions.push({ gw, position: myIdx + 1, sorted });
  }

  if (!gwPositions.length || numManagers < 2) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: '#8a8a8a' }}>No season data available</p>
      </div>
    );
  }

  // SVG layout
  const W = 340, H = 160;
  const padL = 28, padR = 16, padT = 18, padB = 18;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const xOf = (gw) => padL + ((gw - 1) / Math.max(lastGW - 1, 1)) * innerW;
  const yOf = (pos) => padT + ((pos - 1) / Math.max(numManagers - 1, 1)) * innerH;

  // Smooth cubic bezier path
  const pathD = gwPositions.map((d, i) => {
    const x = xOf(d.gw), y = yOf(d.position);
    if (i === 0) return `M ${x} ${y}`;
    const px = xOf(gwPositions[i - 1].gw), py = yOf(gwPositions[i - 1].position);
    const cpx = (px + x) / 2;
    return `C ${cpx} ${py}, ${cpx} ${y}, ${x} ${y}`;
  }).join(' ');

  // Milestone GWs for tick marks
  const step = Math.max(1, Math.round(lastGW / 4));
  const milestones = [];
  for (let gw = step; gw <= lastGW; gw += step) milestones.push(gw);
  if (!milestones.includes(lastGW)) milestones.push(lastGW);

  // Neighbours at season end
  const final = gwPositions[gwPositions.length - 1];
  const myFinalPos = final.position;
  const startPos = gwPositions[0]?.position || myFinalPos;
  const aboveManager = myFinalPos > 1 ? final.sorted[myFinalPos - 2]?.name : null;
  const belowManager = myFinalPos < numManagers ? final.sorted[myFinalPos]?.name : null;

  const positionLabel = (pos) => `${pos}${getOrdinalSuffix(pos)}`;
  const moved = myFinalPos - startPos; // negative = climbed

  return (
    <div className="flex flex-col justify-center h-full px-4 gap-3" style={{ maxWidth: '720px', margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <SlideLabel>Season Journey</SlideLabel>
        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.8 }}
          style={{ fontSize: '12px', fontWeight: 700, color: moved < 0 ? '#44d5a3' : moved > 0 ? '#eb5757' : '#8a8a8a' }}>
          {moved < 0 ? `▲ Climbed ${Math.abs(moved)}` : moved > 0 ? `▼ Fell ${moved}` : '— Unchanged'}
        </motion.span>
      </div>

      {/* Chart */}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
        {/* Grid lines */}
        {[1, Math.ceil(numManagers / 2), numManagers].map((pos) => (
          <line key={pos} x1={padL} y1={yOf(pos)} x2={W - padR} y2={yOf(pos)}
            stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        ))}

        {/* Y-axis labels */}
        <text x={padL - 4} y={yOf(1) + 3} fontSize="8" fill="rgba(255,255,255,0.3)" textAnchor="end">1st</text>
        <text x={padL - 4} y={yOf(numManagers) + 3} fontSize="8" fill="rgba(255,255,255,0.3)" textAnchor="end">Last</text>

        {/* Glow */}
        <motion.path d={pathD} fill="none" stroke="#44d5a3" strokeWidth="5" opacity={0.07}
          strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
          transition={{ duration: 2.4, ease: 'easeInOut', delay: 0.3 }} />

        {/* Main line */}
        <motion.path d={pathD} fill="none" stroke="#44d5a3" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
          transition={{ duration: 2.4, ease: 'easeInOut', delay: 0.3 }} />

        {/* Milestone ticks + labels */}
        {milestones.map((gw) => {
          const d = gwPositions.find((x) => x.gw === gw);
          if (!d) return null;
          const cx = xOf(gw), cy = yOf(d.position);
          const above = cy > H / 2;
          // Position label (e.g. "3rd") — 14px from dot centre
          const posY  = above ? cy - 14 : cy + 22;
          // GW label (e.g. "GW10") — 6px further out from position label
          const gwY   = above ? posY - 9  : posY + 9;
          return (
            <motion.g key={gw} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              transition={{ delay: 0.4 + (gw / lastGW) * 2.2 }}>
              <circle cx={cx} cy={cy} r="3.5" fill="#44d5a3" />
              {/* Dark backdrop prevents label bleeding into the line */}
              <rect x={cx - 14} y={Math.min(posY, gwY) - 8} width="28" height="20"
                rx="3" fill="rgba(0,0,6,0.65)" />
              <text x={cx} y={posY} fontSize="8" fill="rgba(255,255,255,0.7)" textAnchor="middle" fontFamily="Roboto, sans-serif">
                {positionLabel(d.position)}
              </text>
              <text x={cx} y={gwY} fontSize="7" fill="rgba(255,255,255,0.35)" textAnchor="middle" fontFamily="Roboto, sans-serif">
                GW{gw}
              </text>
            </motion.g>
          );
        })}

        {/* Final dot — glowing */}
        <motion.circle cx={xOf(lastGW)} cy={yOf(myFinalPos)} r="6" fill="#44d5a3"
          initial={{ r: 0, opacity: 0 }} animate={{ r: 6, opacity: 1 }}
          transition={{ delay: 2.8, type: 'spring', stiffness: 200, damping: 14 }} />
      </svg>

      {/* Neighbour standings */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 3.0 }}
        style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {aboveManager && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 10px', opacity: 0.45 }}>
            <span style={{ fontSize: '11px', color: '#8a8a8a', minWidth: '24px', textAlign: 'right' }}>{myFinalPos - 1}</span>
            <span style={{ fontSize: '13px', color: '#8a8a8a' }}>{aboveManager}</span>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', backgroundColor: 'rgba(68,213,163,0.1)', borderRadius: '8px', border: '1px solid rgba(68,213,163,0.25)' }}>
          <span style={{ fontSize: '13px', fontWeight: 900, color: '#44d5a3', minWidth: '24px', textAlign: 'right' }}>{myFinalPos}</span>
          <span style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff' }}>{managerName}</span>
        </div>
        {belowManager && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 10px', opacity: 0.45 }}>
            <span style={{ fontSize: '11px', color: '#8a8a8a', minWidth: '24px', textAlign: 'right' }}>{myFinalPos + 1}</span>
            <span style={{ fontSize: '13px', color: '#8a8a8a' }}>{belowManager}</span>
          </div>
        )}
      </motion.div>
    </div>
  );
};


const MomentsSlide = ({ bestGW, bestGWScore, worstGW, worstGWScore, captainSummary, worstTransfer, leaguePosition, totalPoints }) => {
  const moments = [
    bestGW ? { gwNum: bestGW, color: '#f2c94c', label: `GW${bestGW}`, title: 'You Were Unstoppable', desc: `${bestGWScore} pts — your peak performance` } : null,
    worstTransfer ? { gwNum: worstTransfer.gw_sold, color: '#eb5757', label: `GW${worstTransfer.gw_sold}`, title: 'The One That Got Away', desc: `Sold ${worstTransfer.player} · missed ${worstTransfer.points_missed} pts` } : null,
    captainSummary?.Worst_GW ? {
      gwNum: captainSummary.Worst_GW, color: '#3f69ae', label: `GW${captainSummary.Worst_GW}`, title: 'Armband Regret',
      desc: `Backed ${captainSummary.Worst_GW_Actual_Captain || '?'} over ${captainSummary.Worst_GW_Optimal_Captain || '?'} · −${captainSummary.Worst_GW_Points_Diff || 0} pts`,
    } : null,
    worstGW ? { gwNum: worstGW, color: '#4a4a5a', label: `GW${worstGW}`, title: 'The Dark Week', desc: `${worstGWScore} pts — the one you'd rather forget` } : null,
    { gwNum: 999, color: '#44d5a3', label: 'Final', title: 'Final Whistle', desc: `Finished ${leaguePosition} · ${Number(totalPoints).toLocaleString()} pts on the board` },
  ].filter(Boolean).sort((a, b) => a.gwNum - b.gwNum);

  return (
    <div className="flex flex-col justify-center items-center h-full px-6 overflow-y-auto">
      <div style={{ width: '100%', maxWidth: '400px' }}>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
        style={{ fontSize: '14px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#8a8a8a', textAlign: 'center', marginBottom: '14px' }}>
        Season Highlights
      </motion.p>

      {moments.map((moment, i) => (
        <motion.div key={i}
          initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.25 + i * 0.15, duration: 0.35 }}
          style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '6px' }}>

          {/* GW label — left of dot */}
          <div style={{ width: '36px', flexShrink: 0, textAlign: 'right', paddingTop: '10px' }}>
            <span style={{ fontSize: '9px', color: moment.color, fontWeight: 700 }}>{moment.label}</span>
          </div>

          {/* Dot + connector */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: moment.color, marginTop: '8px', flexShrink: 0 }} />
            {i < moments.length - 1 && (
              <div style={{ width: '2px', height: '18px', backgroundColor: 'rgba(255,255,255,0.08)', marginTop: '3px' }} />
            )}
          </div>

          {/* Card */}
          <div style={{
            flex: 1, backgroundColor: 'rgba(255,255,255,0.04)',
            border: `1px solid ${moment.color}35`, borderRadius: '9px',
            padding: '9px 13px',
          }}>
            <p style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', marginBottom: '3px' }}>{moment.title}</p>
            <p style={{ fontSize: '11px', color: '#8a8a8a', lineHeight: 1.4 }}>{moment.desc}</p>
          </div>
        </motion.div>
      ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// SECTION 3 — YOUR DECISIONS
// ═══════════════════════════════════════════════════════════

const CaptainSlide = ({ captainSummary }) => {
  const actual = captainSummary?.Actual_Captain_Points || 0;
  const optimal = captainSummary?.Optimal_Captain_Points || 0;
  const missed = captainSummary?.Points_Missed || 0;
  const pct = captainSummary?.Points_Missed_Percentage
    ? parseFloat(captainSummary.Points_Missed_Percentage).toFixed(1)
    : missed && optimal ? ((missed / optimal) * 100).toFixed(1) : 0;

  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8 gap-5">
      <SlideLabel>Captain Decisions</SlideLabel>

      <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}
          style={{ backgroundColor: 'rgba(63,105,174,0.15)', border: '1px solid rgba(63,105,174,0.35)', borderRadius: '12px', padding: '16px 20px' }}>
          <p style={{ fontSize: '10px', color: '#8a8a8a', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Actual</p>
          <p style={{ fontSize: '36px', fontWeight: 900, color: '#3f69ae', lineHeight: 1 }}>{actual}</p>
          <p style={{ fontSize: '11px', color: '#8a8a8a', marginTop: '2px' }}>pts</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 }}
          style={{ backgroundColor: 'rgba(68,213,163,0.1)', border: '1px solid rgba(68,213,163,0.3)', borderRadius: '12px', padding: '16px 20px' }}>
          <p style={{ fontSize: '10px', color: '#8a8a8a', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Optimal</p>
          <p style={{ fontSize: '36px', fontWeight: 900, color: '#44d5a3', lineHeight: 1 }}>{optimal}</p>
          <p style={{ fontSize: '11px', color: '#8a8a8a', marginTop: '2px' }}>pts</p>
        </motion.div>
      </div>

      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        style={{ fontSize: 'clamp(28px,7vw,44px)', fontWeight: 900, color: '#eb5757' }}>
        -{missed} pts missed
      </motion.p>
      {pct > 0 && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
          style={{ fontSize: '13px', color: '#8a8a8a' }}>
          {pct}% of optimal captain points left on the table
        </motion.p>
      )}
    </div>
  );
};

const VillainSlide = ({ worstTransfer, captainSummary, loyaltyDetails, captainDetailedChoices }) => {
  const worstCaptainGW = captainSummary?.Worst_GW;
  const worstActualCaptain = captainSummary?.Worst_GW_Actual_Captain;
  const worstOptimalCaptain = captainSummary?.Worst_GW_Optimal_Captain;
  const worstCaptainDiff = captainSummary?.Worst_GW_Points_Diff || 0;

  // Mixed regrets: ALL transfers + ALL bad captain GWs, sorted by impact, max 5
  const transferItems = [...(loyaltyDetails || [])]
    .sort((a, b) => (b.points_missed || 0) - (a.points_missed || 0))
    .slice(1) // skip the single worst already featured in the card above
    .map((t) => ({ label: t.player, detail: `Sold GW${t.gw_sold} · scored GW${t.gw_haul}`, pts: t.points_missed, tag: 'Transfer' }));

  const captainItems = [...(captainDetailedChoices || [])]
    .filter((c) => (c.points_diff || 0) > 0)
    .sort((a, b) => (a.points_diff || 0) - (b.points_diff || 0)) // most negative first
    .filter((c) => c.gw !== worstCaptainGW) // skip the one already featured
    .map((c) => ({ label: `GW${c.gw} captain`, detail: `${Math.abs(c.points_diff || 0)} pts below optimal pick`, pts: Math.abs(c.points_diff || 0), tag: 'Captain' }));

  const mixedRegrets = [...transferItems, ...captainItems]
    .sort((a, b) => b.pts - a.pts)
    .slice(0, 5);

  const hasData = worstTransfer || (captainSummary && worstActualCaptain);
  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8 gap-4">
        <p style={{ fontSize: '18px', fontWeight: 700, color: '#8a8a8a' }}>No villain data recorded</p>
      </div>
    );
  }

  return (
    <div style={{ overflowY: 'auto', height: '100%' }}>
    <div className="flex flex-col items-center justify-center px-5 py-5 gap-5" style={{ minHeight: '100%' }}>
      <SlideLabel color="#eb5757">Villain Moments</SlideLabel>

      {/* Two square villain cards */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        {worstTransfer && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 150, damping: 20 }}
            style={{
              width: '148px', minHeight: '148px', flexShrink: 0,
              backgroundColor: 'rgba(235,87,87,0.08)', border: '1px solid rgba(235,87,87,0.28)',
              borderRadius: '16px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px',
            }}>
            <p style={{ fontSize: '9px', color: '#8a8a8a', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>
              Worst Transfer
            </p>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '14px', fontWeight: 900, color: '#ffffff', lineHeight: 1.3, marginBottom: '4px' }}>{worstTransfer.player}</p>
              <p style={{ fontSize: '10px', color: '#8a8a8a', lineHeight: 1.4, margin: 0 }}>GW{worstTransfer.gw_sold} → GW{worstTransfer.gw_haul}</p>
            </div>
            <div>
              <p style={{ fontSize: '26px', fontWeight: 900, color: '#eb5757', lineHeight: 1, margin: 0 }}>-{worstTransfer.points_missed}</p>
              <p style={{ fontSize: '10px', color: '#8a8a8a', marginTop: '3px', marginBottom: 0 }}>pts missed</p>
            </div>
          </motion.div>
        )}

        {captainSummary && worstActualCaptain && (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, type: 'spring', stiffness: 150, damping: 20 }}
            style={{
              width: '148px', minHeight: '148px', flexShrink: 0,
              backgroundColor: 'rgba(235,87,87,0.08)', border: '1px solid rgba(235,87,87,0.28)',
              borderRadius: '16px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px',
            }}>
            <p style={{ fontSize: '9px', color: '#8a8a8a', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>
              Worst Captain
            </p>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '14px', fontWeight: 900, color: '#ffffff', lineHeight: 1.3, marginBottom: '4px' }}>{worstActualCaptain}</p>
              <p style={{ fontSize: '10px', color: '#8a8a8a', lineHeight: 1.5, margin: 0 }}>
                {worstCaptainGW ? `GW${worstCaptainGW}` : ''}
                {worstOptimalCaptain ? ` · should've been ${worstOptimalCaptain}` : ''}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '26px', fontWeight: 900, color: '#eb5757', lineHeight: 1, margin: 0 }}>-{worstCaptainDiff}</p>
              <p style={{ fontSize: '10px', color: '#8a8a8a', marginTop: '3px', marginBottom: 0 }}>pts missed</p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Mixed regrets list */}
      {mixedRegrets.length > 0 && (
        <div style={{ width: '100%', maxWidth: '360px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
          <p style={{ fontSize: '10px', color: '#8a8a8a', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', marginBottom: '2px' }}>
            Decisions you'll regret
          </p>
          {mixedRegrets.map((item, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 + i * 0.09 }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                backgroundColor: 'rgba(235,87,87,0.05)', border: '1px solid rgba(235,87,87,0.14)',
                borderRadius: '9px', padding: '12px 14px',
              }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</p>
                  <span style={{ fontSize: '8px', fontWeight: 700, color: item.tag === 'Captain' ? '#f2c94c' : '#eb5757', backgroundColor: item.tag === 'Captain' ? 'rgba(242,201,76,0.12)' : 'rgba(235,87,87,0.12)', borderRadius: '4px', padding: '1px 5px', textTransform: 'uppercase', flexShrink: 0 }}>
                    {item.tag}
                  </span>
                </div>
                <p style={{ fontSize: '11px', color: '#8a8a8a', lineHeight: 1.4 }}>{item.detail}</p>
              </div>
              <p style={{ fontSize: '18px', fontWeight: 900, color: '#eb5757', marginLeft: '12px', flexShrink: 0 }}>-{item.pts}</p>
            </motion.div>
          ))}
        </div>
      )}
    </div>
    </div>
  );
};

const TinkermanSlide = ({ transferCost, rawTotal }) => {
  const pct = rawTotal > 0 ? ((transferCost / rawTotal) * 100).toFixed(1) : 0;
  return (
    <div className="flex flex-col items-center justify-center h-full px-8 gap-5 text-center">
      <SlideLabel>The Tinkerman</SlideLabel>
      {transferCost > 0 ? (
        <>
          <motion.p initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 160, damping: 18 }}
            style={{ fontSize: 'clamp(52px,13vw,88px)', fontWeight: 900, color: '#f2c94c', lineHeight: 1 }}>
            -{transferCost}
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            style={{ fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
            points spent on transfers
          </motion.p>
          {pct > 0 && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
              style={{ fontSize: '13px', color: '#8a8a8a' }}>
              {pct}% of your raw score before deductions
            </motion.p>
          )}
        </>
      ) : (
        <>
          <p style={{ fontSize: '40px' }}>✨</p>
          <p style={{ fontSize: '20px', fontWeight: 700, color: '#44d5a3' }}>Free Transfer Guru</p>
          <p style={{ fontSize: '14px', color: '#8a8a8a' }}>No points deducted for transfers this season</p>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// SECTION 4 — YOUR EDGE
// ═══════════════════════════════════════════════════════════

const DifferentialSlide = ({ diffSummary }) => {
  const bestPlayer = diffSummary?.Best_Differential_Player || null;
  const bestPoints = diffSummary?.Best_Differential_Points || 0;
  const bestOwnership = parseFloat(diffSummary?.Best_Differential_Ownership || diffSummary?.Best_Differential_Ownership_Percent || 0).toFixed(1);
  const totalDiffPoints = diffSummary?.Differential_Points || 0;
  const diffCount = diffSummary?.Differential_Count || 0;

  if (!bestPlayer && !totalDiffPoints) {
    return (
      <div className="flex flex-col items-center justify-center h-full px-8 gap-4 text-center">
        <p style={{ fontSize: '18px', fontWeight: 700, color: '#8a8a8a' }}>No differential data available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-full px-8 gap-5 text-center">
      <SlideLabel>Differential Genius</SlideLabel>

      {bestPlayer && (
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, type: 'spring', stiffness: 150, damping: 18 }}
          style={{ backgroundColor: 'rgba(68,213,163,0.1)', border: '1px solid rgba(68,213,163,0.3)', borderRadius: '16px', padding: '20px 28px' }}>
          <p style={{ fontSize: '11px', color: '#8a8a8a', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>
            Best hidden gem
          </p>
          <p style={{ fontSize: 'clamp(20px,5vw,32px)', fontWeight: 900, color: '#44d5a3' }}>{bestPlayer}</p>
          <p style={{ fontSize: '13px', color: '#8a8a8a', marginTop: '6px' }}>{bestPoints} pts · only {bestOwnership}% owned</p>
        </motion.div>
      )}

      <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
        style={{ fontSize: 'clamp(28px,7vw,44px)', fontWeight: 900, color: '#44d5a3' }}>
        {totalDiffPoints} pts
      </motion.p>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
        style={{ fontSize: '13px', color: '#8a8a8a' }}>
        from {diffCount} differential pick{diffCount !== 1 ? 's' : ''} the rest of the league didn't own
      </motion.p>
    </div>
  );
};

const BenchSlide = ({ benchPoints, totalPoints }) => {
  const pct = totalPoints > 0 ? ((benchPoints / totalPoints) * 100).toFixed(1) : 0;
  const avgPerGW = (benchPoints / 38).toFixed(1);
  return (
    <div className="flex flex-col items-center justify-center h-full px-8 gap-5 text-center">
      <SlideLabel>Your 12th Man</SlideLabel>
      <motion.p initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 160, damping: 18 }}
        style={{ fontSize: 'clamp(56px,14vw,92px)', fontWeight: 900, color: '#3f69ae', lineHeight: 1 }}>
        {benchPoints}
      </motion.p>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
        style={{ fontSize: '16px', fontWeight: 600, color: '#ffffff' }}>
        points stranded on your bench
      </motion.p>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
        style={{ fontSize: '13px', color: '#8a8a8a' }}>
        ~{avgPerGW} pts per gameweek · {pct}% of your total score
      </motion.p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// SECTION 5 — WHO YOU ARE
// ═══════════════════════════════════════════════════════════

const ArchetypeSlide = ({ archetype }) => {
  const info = ARCHETYPE_INFO[archetype] || null;
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-8 gap-4">
      <SlideLabel>Your FPL Archetype</SlideLabel>

      {info?.image && (
        <motion.img
          src={info.image}
          alt={archetype}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
          initial={{ opacity: 0, scale: 0.75 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.25, type: 'spring', stiffness: 150, damping: 18 }}
          style={{ width: '110px', height: '110px', objectFit: 'cover', borderRadius: '50%', border: '2px solid rgba(68,213,163,0.45)' }}
        />
      )}

      <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, type: 'spring', stiffness: 160, damping: 18 }}
        style={{
          padding: '14px 28px', borderRadius: '14px',
          background: 'linear-gradient(135deg, rgba(63,105,174,0.2) 0%, rgba(68,213,163,0.14) 100%)',
          border: '2px solid rgba(68,213,163,0.35)',
        }}>
        <h2 style={{ fontSize: 'clamp(20px,5vw,34px)', fontWeight: 900, color: '#44d5a3', lineHeight: 1.2 }}>
          {archetype || 'FPL Enthusiast'}
        </h2>
      </motion.div>

      {info?.description && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
          style={{ fontSize: '13px', color: '#8a8a8a', lineHeight: 1.55, maxWidth: '300px' }}>
          {info.description}
        </motion.p>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// SECTION 6 — THE VERDICT
// ═══════════════════════════════════════════════════════════

const FinalSlide = () => (
  <div className="flex flex-col items-center justify-center h-full overflow-y-auto py-4 px-4"
    onClick={(e) => e.stopPropagation()}>
    <ShareableSeasonCard />
  </div>
);

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

const SeasonStoryDeck = ({ isOpen, onClose }) => {
  const { data } = useLeagueData();
  const { selectedManager, getManagerData } = useManagerContext();
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (isOpen) setCurrentSlide(0);
  }, [isOpen]);

  const advance = useCallback(() => {
    setCurrentSlide((s) => {
      if (s < SECTION_MAP.length - 1) return s + 1;
      onClose();
      return s;
    });
  }, [onClose]);

  const retreat = useCallback(() => {
    setCurrentSlide((s) => Math.max(0, s - 1));
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowRight' || e.key === ' ') advance();
      else if (e.key === 'ArrowLeft') retreat();
      else if (e.key === 'Escape') onClose();
    },
    [isOpen, advance, retreat, onClose]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!selectedManager || !data) return null;

  const managerData = getManagerData();
  const totalPoints = managerData?.totalPoints || 0;
  const leaguePosition = managerData?.leaguePosition || '—';
  const captainSummary = managerData?.captainHindsight?.summary || null;
  const loyaltyDetails = managerData?.loyaltyIssues?.details || [];
  const diffSummary = managerData?.differentialPlayers?.summary || null;
  const benchPoints = managerData?.benchPoints || 0;

  const worstTransfer = [...loyaltyDetails]
    .sort((a, b) => (b.points_missed || 0) - (a.points_missed || 0))[0] || null;

  const bestMonth = deriveBestMonth(data.monthlyPoints, selectedManager, data.monthlyTopPerformers);

  const primaryArchetype =
    data['Player Archetypes']?.find((r) => r.Manager === selectedManager)?.Primary_Archetype ||
    'FPL Enthusiast';

  const tinkermanRow = data?.weeklyPoints?.find(
    (row) => row.Manager === selectedManager && row.Total_Transfer_Cost !== undefined
  );
  const transferCost = tinkermanRow?.Total_Transfer_Cost || 0;
  const rawTotal = tinkermanRow?.Raw_Total || 0;

  // Best / worst GW
  let bestGW = null, bestGWScore = 0, worstGW = null, worstGWScore = Infinity;
  const gwRow = data?.weeklyPoints?.find((r) => r.Manager === selectedManager);
  if (gwRow) {
    for (let gw = 1; gw <= 38; gw++) {
      const val = gwRow[gw];
      if (val !== undefined && val !== null) {
        const score = Number(val);
        if (!isNaN(score)) {
          if (score > bestGWScore) { bestGWScore = score; bestGW = gw; }
          if (score < worstGWScore) { worstGWScore = score; worstGW = gw; }
        }
      }
    }
  }
  if (worstGWScore === Infinity) worstGWScore = 0;

  const captainDetailedChoices = (data['Detailed Captain Choices'] || [])
    .filter((r) => r.Manager === selectedManager);

  const TOTAL = SECTION_MAP.length;

  const slides = [
    { key: 'intro',    content: <IntroSlide managerName={selectedManager} /> },
    { key: 'rank',     content: <RankSlide leaguePosition={leaguePosition} totalPoints={totalPoints} /> },
    { key: 'month',    content: <BestMonthSlide bestMonth={bestMonth} /> },
    { key: 'momentum', content: <SeasonMomentumSlide weeklyPoints={data.weeklyPoints} managerName={selectedManager} transferCostsGW={data.transferCostsGW} bestGW={bestGW} bestGWScore={bestGWScore} worstGW={worstGW} worstGWScore={worstGWScore} /> },
    { key: 'moments',  content: <MomentsSlide bestGW={bestGW} bestGWScore={bestGWScore} worstGW={worstGW} worstGWScore={worstGWScore} captainSummary={captainSummary} worstTransfer={worstTransfer} leaguePosition={leaguePosition} totalPoints={totalPoints} /> },
    { key: 'captain',  content: <CaptainSlide captainSummary={captainSummary} /> },
    { key: 'villain',  content: <VillainSlide worstTransfer={worstTransfer} captainSummary={captainSummary} loyaltyDetails={loyaltyDetails} captainDetailedChoices={captainDetailedChoices} /> },
    { key: 'tinkerman',content: <TinkermanSlide transferCost={transferCost} rawTotal={rawTotal} /> },
    { key: 'diff',     content: <DifferentialSlide diffSummary={diffSummary} /> },
    { key: 'bench',    content: <BenchSlide benchPoints={benchPoints} totalPoints={totalPoints} /> },
    { key: 'archetype',content: <ArchetypeSlide archetype={primaryArchetype} /> },
    { key: 'final',    content: <FinalSlide /> },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="story-deck-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          style={{ position: 'fixed', inset: 0, zIndex: 50, backgroundColor: '#000006', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
          onClick={advance}
        >
          <DotBackground />
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 50%, rgba(63,105,174,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

          {/* Top bar */}
          <div
            style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 20px 12px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#44d5a3' }}>
                {SECTION_MAP[currentSlide]}
              </span>
              <ProgressDots total={TOTAL} current={currentSlide} />
            </div>
            <button onClick={onClose} aria-label="Close story"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#8a8a8a', flexShrink: 0 }}>
              <X size={16} />
            </button>
          </div>

          {/* Slide content */}
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={slides[currentSlide].key}
                variants={slideVariants} initial="enter" animate="centre" exit="exit"
                transition={slideTransition}
                style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}
              >
                {slides[currentSlide].content}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Bottom nav */}
          <div
            style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 28px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={retreat} disabled={currentSlide === 0} aria-label="Previous slide"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: currentSlide === 0 ? 'default' : 'pointer', color: currentSlide === 0 ? 'rgba(138,138,138,0.3)' : '#8a8a8a' }}>
              <ChevronLeft size={18} />
            </button>

            <p style={{ fontSize: '12px', color: 'rgba(138,138,138,0.6)' }}>
              {currentSlide + 1} / {TOTAL}
            </p>

            <button onClick={advance} aria-label={currentSlide === TOTAL - 1 ? 'Close story' : 'Next slide'}
              style={{ background: 'rgba(68,213,163,0.12)', border: '1px solid rgba(68,213,163,0.3)', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#44d5a3' }}>
              <ChevronRight size={18} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SeasonStoryDeck;
